import { useEffect, useState } from "react";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Loader2, Users, Search, Activity, Clock } from "lucide-react";
import { api } from "@/lib/api";
import {
  PAYMENT_LABELS,
  STATUS_LABELS,
  fmtDate,
  paymentBadgeClass,
  statusBadgeClass,
  teacherEnrollments,
} from "@/lib/enrollments";

export default function TeacherStudents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Phase 22 Item 4 — cached usage lookup keyed by course_id → studentId → row.
  const [usageByCourse, setUsageByCourse] = useState({});
  const [openUsage, setOpenUsage] = useState(null); // {row, data} | null
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    teacherEnrollments()
      .then((rs) => {
        setRows(rs);
        // Pre-fetch usage aggregates for every unique course in the roster.
        const courseIds = [...new Set(rs.map((r) => r.course_id))];
        Promise.all(
          courseIds.map((cid) =>
            api
              .get(`/teacher/courses/${cid}/usage`)
              .then((res) => [cid, res.data])
              .catch(() => [cid, null])
          )
        ).then((pairs) => {
          const map = {};
          pairs.forEach(([cid, data]) => {
            if (!data) return;
            map[cid] = {};
            (data.students || []).forEach((s) => (map[cid][s.student_id] = s));
          });
          setUsageByCourse(map);
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const openUsageDrawer = async (row) => {
    setOpenUsage({ row, data: null });
    setUsageLoading(true);
    try {
      const { data } = await api.get(
        `/teacher/courses/${row.course_id}/usage`,
        { params: { student_id: row.student_id } }
      );
      setOpenUsage({ row, data });
    } finally {
      setUsageLoading(false);
    }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const haystack = `${r.student_name} ${r.student_email} ${r.course_title}`.toLowerCase();
    return haystack.includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="teacher-students">
      <DashboardHeader subtitle="Teacher · My students" />
      <main className="mx-auto max-w-7xl px-6 py-8 space-y-4">
        <BackButton to="/teacher" />
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Users className="h-6 w-6 text-amber-600" /> My students
            </h1>
            <p className="text-sm text-stone-500">
              Every student the admin has mapped to one of your published courses.
            </p>
          </div>
        </div>

        <Card className="border-stone-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">
              {filtered.length} student{filtered.length === 1 ? "" : "s"}
            </CardTitle>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-stone-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email or course"
                className="pl-8"
                data-testid="student-search"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-10 text-center text-stone-500">
                <Loader2 className="mx-auto h-5 w-5 animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="py-10 text-center text-sm text-stone-500"
                data-testid="no-students"
              >
                No students mapped yet. Ask the admin to map students from
                Admin → Enrollments.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="teacher-students-table">
                  <thead className="bg-stone-50 text-stone-600 text-xs uppercase">
                    <tr>
                      <th className="text-left py-2 px-3">Student</th>
                      <th className="text-left py-2 px-3">Course</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-left py-2 px-3">Payment</th>
                      <th className="text-left py-2 px-3">Mapped</th>
                      <th className="text-left py-2 px-3">Consumed</th>
                      <th className="text-left py-2 px-3">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-stone-100"
                        data-testid={`teacher-student-row-${r.id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="font-medium">{r.student_name}</div>
                          <div className="text-xs text-stone-500">{r.student_email}</div>
                        </td>
                        <td className="py-2 px-3 max-w-[260px] truncate">
                          {r.course_title}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className={statusBadgeClass(r.status)}>
                              {STATUS_LABELS[r.status]}
                            </Badge>
                            {r.self_enrolled && (
                              <Badge
                                variant="outline"
                                className="border-amber-300 text-amber-800 bg-amber-50 text-[10px]"
                                data-testid={`self-enrolled-pill-${r.id}`}
                                title="Student self-enrolled via public catalog"
                              >
                                🎫 Self-enrolled
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <Badge
                            variant="outline"
                            className={paymentBadgeClass(r.payment_status)}
                          >
                            {PAYMENT_LABELS[r.payment_status]}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-xs text-stone-500">
                          {fmtDate(r.created_at)}
                        </td>
                        <td className="py-2 px-3">
                          <UsageCell
                            row={r}
                            usage={usageByCourse[r.course_id]?.[r.student_id]}
                            onOpen={() => openUsageDrawer(r)}
                          />
                        </td>
                        <td className="py-2 px-3 text-xs text-stone-600 max-w-[200px] truncate">
                          {r.notes || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <UsageDrawer
        open={!!openUsage}
        onClose={() => setOpenUsage(null)}
        row={openUsage?.row}
        data={openUsage?.data}
        loading={usageLoading}
      />
    </div>
  );
}

function UsageCell({ row, usage, onOpen }) {
  if (!usage) {
    return (
      <span
        className="text-xs text-stone-400"
        data-testid={`usage-cell-empty-${row.id}`}
      >
        no activity
      </span>
    );
  }
  const c = usage.counts;
  const total = c.chapters_viewed + c.homework_submitted + c.tests_taken + c.sessions_joined;
  return (
    <button
      onClick={onOpen}
      className="text-left group"
      data-testid={`usage-cell-${row.id}`}
    >
      <div className="text-sm font-medium text-stone-800 group-hover:text-amber-700">
        {total} events · {usage.hours_estimate}h
      </div>
      <div className="text-[11px] text-stone-500">
        {c.chapters_viewed} ch · {c.tests_taken} tests · {c.sessions_joined} sessions
      </div>
    </button>
  );
}

function UsageDrawer({ open, onClose, row, data, loading }) {
  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md overflow-y-auto"
        data-testid="teacher-usage-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-amber-600" />
            Content activity
          </SheetTitle>
          <SheetDescription>
            {row?.student_name} · {row?.course_title}
          </SheetDescription>
        </SheetHeader>
        {loading || !data ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-stone-500" />
          </div>
        ) : (
          <UsageDrawerBody data={data} studentId={row?.student_id} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function UsageDrawerBody({ data, studentId }) {
  const summary = (data.students || []).find((s) => s.student_id === studentId);
  const timeline = data.timeline || [];
  if (!summary) {
    return (
      <div className="py-6 text-sm text-stone-500 text-center">
        No activity recorded yet.
      </div>
    );
  }
  const c = summary.counts;
  const cards = [
    { label: "Chapters viewed", value: c.chapters_viewed },
    { label: "Classwork completed", value: c.classwork_completed },
    { label: "Homework submitted", value: c.homework_submitted },
    { label: "Tests taken", value: c.tests_taken },
    { label: "Book PDFs", value: c.book_pdfs_downloaded },
    { label: "Study pack PDFs", value: c.study_pack_downloaded },
    { label: "Sessions joined", value: c.sessions_joined },
  ];
  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-800">
          <Clock className="h-4 w-4" /> Estimated time
        </div>
        <div className="text-lg font-semibold text-amber-900">
          {summary.hours_estimate}h
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        {cards.map((c) => (
          <div
            key={c.label}
            className="rounded border border-stone-200 p-2"
            data-testid={`usage-drawer-stat-${c.label.replace(/\s/g, "-").toLowerCase()}`}
          >
            <div className="text-[10px] uppercase text-stone-500">{c.label}</div>
            <div className="text-lg font-semibold">{c.value}</div>
          </div>
        ))}
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">
          Recent events
        </div>
        {timeline.length === 0 ? (
          <div className="text-xs text-stone-500 py-2">Nothing yet.</div>
        ) : (
          <ul className="space-y-1 text-xs">
            {timeline.slice(0, 15).map((row) => (
              <li
                key={row.id}
                className="flex justify-between gap-3 rounded border border-stone-100 px-2 py-1.5"
              >
                <span className="text-stone-700">{row.event_type.replace(/_/g, " ")}</span>
                <span className="text-stone-400">
                  {new Date(row.at).toLocaleString(undefined, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
